import { AddLocationAlt, PlaceOutlined } from "@mui/icons-material";
import type { FC } from "react";

import { MAP_DATA_CONTROLS_ATTR } from "@ryde/shared";

import type { IMapLibrePlaceInfoLookup } from "./maplibre-place-info.models";
import { MapLibrePlaceInfoCardStyled } from "./maplibre-place-info.styled";
import { useMapLibrePlaceInfoCard } from "./use-maplibre-place-info-card";

interface IMapLibrePlaceInfoCardProps {
  lookup: IMapLibrePlaceInfoLookup;
  onRetry: () => void;
  onStationAdded: () => void;
}

/**
 * What is at the clicked point. Marked as map controls: a popup sits inside the map's canvas container,
 * so a click on it also reaches the screens' own map-click handlers, which skip that marker.
 */
export const MapLibrePlaceInfoCard: FC<IMapLibrePlaceInfoCardProps> = ({
  lookup,
  onRetry,
  onStationAdded,
}) => {
  const {
    t,
    display,
    coordinateText,
    isLoading,
    isEmpty,
    isFailed,
    failureText,
    canRetry,
    canAddStation,
    isAddStationDisabled,
    isFullscreenKept,
    handleAddStation,
  } = useMapLibrePlaceInfoCard({ lookup, onStationAdded });

  return (
    <MapLibrePlaceInfoCardStyled
      {...{ [MAP_DATA_CONTROLS_ATTR]: true }}
      aria-live="polite"
      aria-busy={isLoading}
    >
      <div className="place-info-header">
        <PlaceOutlined className="place-info-icon" aria-hidden />

        <div className="place-info-texts">
          {isLoading && (
            <p className="place-info-message">{t("ui.PLACE_INFO_LOADING")}</p>
          )}

          {display && <p className="place-info-title">{display.title}</p>}

          {display?.address && (
            <p className="place-info-address">{display.address}</p>
          )}

          {isEmpty && (
            <p className="place-info-message">
              {t("ui.PLACE_INFO_NO_ADDRESS")}
            </p>
          )}

          {isFailed && (
            <p className="place-info-message">
              {failureText}
              {canRetry && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="place-info-retry"
                    onClick={onRetry}
                  >
                    {t("ui.PLACE_INFO_RETRY")}
                  </button>
                </>
              )}
            </p>
          )}

          <p
            className="place-info-coordinates"
            title={t("ui.PLACE_INFO_COORDINATES")}
          >
            <bdi dir="ltr">{coordinateText}</bdi>
          </p>
        </div>
      </div>

      {canAddStation && (
        <div className="place-info-actions">
          <button
            type="button"
            className="place-info-add-station"
            disabled={isAddStationDisabled}
            onClick={handleAddStation}
          >
            <AddLocationAlt aria-hidden />
            {t("ui.PLACE_INFO_ADD_STATION")}
          </button>

          {isFullscreenKept && (
            <p className="place-info-message">
              {t("ui.PLACE_INFO_ADD_STATION_FULLSCREEN")}
            </p>
          )}
        </div>
      )}
    </MapLibrePlaceInfoCardStyled>
  );
};
